import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, Target, Layers, FileText, Loader2, Rocket, Archive, RotateCcw, ChevronDown, ChevronRight, Compass, Settings, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { productsApi, teamApi, productWizardApi } from '../services/api';
import { useSafeMode } from '../contexts/SafeModeContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import UpgradeContextModal from '../components/billing/UpgradeContextModal';
import UsageBanner from '../components/billing/UsageBanner';
import { useLimitCheck } from '../hooks/useLimitCheck';
import ProductSettingsModal from '../components/product/ProductSettingsModal';

// Helper: Get contrast color for text on colored backgrounds
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#1f2937' : '#ffffff';
};

// Helper: Darken a hex color by a percentage
const darkenColor = (hexColor, percent = 20) => {
  if (!hexColor) return '#1f2937';
  const hex = hexColor.replace('#', '');
  const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - Math.round(255 * (percent / 100)));
  const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - Math.round(255 * (percent / 100)));
  const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - Math.round(255 * (percent / 100)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const { safeMode, getThemeColors } = useSafeMode();
  const { canCrudProducts, role } = usePermissions();
  const colors = getThemeColors();
  const [products, setProducts] = useState([]);
  const [archivedProducts, setArchivedProducts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(null); // product_id being archived/restored
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsProduct, setSettingsProduct] = useState(null);
  
  // Archive confirmation modal state
  const [archiveConfirm, setArchiveConfirm] = useState({ show: false, product: null });
  
  // Delete draft confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, product: null });
  const [deleting, setDeleting] = useState(false);
  
  // Check if user can see settings (owner or product_manager)
  const canSeeSettings = role === 'owner' || role === 'product_manager' || role === 'admin';
  
  // Team members for settings modal
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Limit check hook
  const { 
    isChecking, 
    showUpgradeModal, 
    currentUsage, 
    currentLimit, 
    checkProductLimit, 
    closeModal 
  } = useLimitCheck();

  const loadProducts = async () => {
    try {
      setLoading(true);
      const [activeRes, archivedRes] = await Promise.all([
        productsApi.list(),
        productsApi.listArchived()
      ]);
      
      // Sort products newest to oldest
      const sortByNewest = (a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA; // Descending (newest first)
      };
      
      setProducts((activeRes.data || []).sort(sortByNewest));
      setArchivedProducts((archivedRes.data || []).sort(sortByNewest));
      setError(null);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // Handle new product creation with limit check
  const handleNewProduct = async () => {
    const { canCreate } = await checkProductLimit();
    if (canCreate) {
      navigate('/products/new');
    }
    // If can't create, the modal will show automatically
  };

  // Show archive confirmation modal
  const showArchiveConfirm = (e, product) => {
    e.stopPropagation();
    setArchiveConfirm({ show: true, product });
  };

  // Archive a product (called after confirmation)
  const handleArchive = async () => {
    const product = archiveConfirm.product;
    if (!product) return;
    
    setArchiveConfirm({ show: false, product: null });
    setArchiving(product.product_id);
    try {
      await productsApi.archive(product.product_id);
      toast.success('Product archived', { description: `${product.name} has been archived` });
      await loadProducts(); // Refresh data
    } catch (err) {
      console.error('Error archiving product:', err);
      toast.error('Failed to archive product');
    } finally {
      setArchiving(null);
    }
  };

  // Open settings modal for a product
  const openSettings = async (e, product) => {
    e.stopPropagation();
    setSettingsProduct(product);
    setShowSettingsModal(true);
    
    // Load team members for the settings modal
    try {
      const teamData = await teamApi.list();
      const membersArray = teamData.data?.members || teamData.data || [];
      const members = (Array.isArray(membersArray) ? membersArray : []).map(m => ({
        id: m.user_id,
        user_id: m.user_id,
        name: m.name || m.email?.split('@')[0] || 'Unknown',
        email: m.email,
        role: m.role
      }));
      setTeamMembers(members);
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  // Handle settings update
  const handleSettingsUpdate = (updatedProduct) => {
    // If no product data passed, just refresh the list
    if (!updatedProduct || !updatedProduct.product_id) {
      loadProducts();
      return;
    }
    setProducts(prev => prev.map(p => 
      p.product_id === updatedProduct.product_id ? { ...p, ...updatedProduct } : p
    ));
  };

  // Restore an archived product
  const handleUnarchive = async (e, productId, productName) => {
    e.stopPropagation();
    setArchiving(productId);
    try {
      await productsApi.unarchive(productId);
      toast.success('Product restored', { description: `${productName} has been restored` });
      await loadProducts(); // Refresh data
    } catch (err) {
      console.error('Error restoring product:', err);
      const message = err.response?.data?.detail || 'Failed to restore product';
      toast.error(message);
    } finally {
      setArchiving(null);
    }
  };

  // Delete a draft product
  const handleDeleteDraft = async () => {
    const product = deleteConfirm.product;
    if (!product?.draft_hash) return;
    setDeleting(true);
    setDeleteConfirm({ show: false, product: null });
    try {
      await productWizardApi.deleteDraft(product.draft_hash);
      toast.success('Draft deleted', { description: `${product.draft_data?.canvas?.product?.name || 'Draft'} has been deleted` });
      await loadProducts();
    } catch (err) {
      console.error('Error deleting draft:', err);
      toast.error('Failed to delete draft');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className={cn("w-8 h-8 animate-spin mx-auto mb-4", colors.textAccent)} />
            <p className={colors.textSecondary}>
              {safeMode ? "Loading products..." : "Initializing..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          title="Products"
          action={
            canCrudProducts ? (
              <Button 
                onClick={handleNewProduct}
                disabled={isChecking}
                className={cn(colors.accentGradient, colors.accentGradientHover, colors.accentShadow)}
              >
                {isChecking ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : safeMode ? (
                  <Plus className="w-4 h-4 mr-2" />
                ) : (
                  <Rocket className="w-4 h-4 mr-2" />
                )}
                {safeMode ? "New Product" : "Launch Product"}
              </Button>
            ) : null
          }
        />
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-7xl mx-auto">
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Usage banner - shows when approaching product limit */}
            <UsageBanner 
              limitType="products" 
              showAtPercentage={50}
              className="mb-6"
            />

            {products.length === 0 ? (
              <Card className={cn(
                "p-12 text-center border",
                colors.bgCard,
                colors.borderPrimary
              )}>
                {safeMode ? (
                  <Layers className={cn("w-16 h-16 mx-auto mb-4", colors.textMuted)} />
                ) : (
                  <Rocket className={cn("w-16 h-16 mx-auto mb-4", colors.textAccent)} />
                )}
                <h3 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
                  {safeMode ? "No products yet" : "Ready to Build"}
                </h3>
                <p className={cn("mb-6 max-w-md mx-auto", colors.textSecondary)}>
                  {canCrudProducts 
                    ? (safeMode 
                        ? "Create your first product to start managing your roadmap with AI-powered insights"
                        : "Launch your first product. Drop a URL, upload a deck, or just describe your vision.")
                    : "No products have been created yet. Contact your team owner or Product Manager to create products."
                  }
                </p>
                {canCrudProducts && (
                  <Button 
                    onClick={handleNewProduct}
                    disabled={isChecking}
                    className={cn(colors.accentGradient, colors.accentGradientHover, colors.accentShadow)}
                  >
                    {isChecking ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : safeMode ? (
                      <Plus className="w-4 h-4 mr-2" />
                    ) : (
                      <Rocket className="w-4 h-4 mr-2" />
                    )}
                    {safeMode ? "Create Your First Product" : "Launch Your First Product"}
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map((product) => {
                  const isDraft = product.status === 'draft';
                  const hasCustomColor = !isDraft && !!product.card_color;
                  const textColor = hasCustomColor ? getContrastColor(product.card_color) : undefined;
                  
                  // For drafts, pull name/description from draft_data.canvas
                  const displayName = isDraft 
                    ? (product.draft_data?.canvas?.product?.name || 'Untitled Draft')
                    : product.name;
                  const displayDescription = isDraft
                    ? (product.draft_data?.canvas?.product?.description || 'Continue building this product...')
                    : product.high_level_vision;
                  
                  const handleCardClick = () => {
                    if (isDraft) {
                      navigate(`/products/new?draft=${product.product_id}`);
                    } else {
                      navigate(`/products/${product.product_id}${product.default_tab ? `?tab=${product.default_tab}` : ''}`);
                    }
                  };
                  
                  return (
                  <Card
                    key={product.product_id}
                    className={cn(
                      "p-6 transition-all cursor-pointer group border flex flex-col h-full min-h-[200px]",
                      isDraft && "border-dashed border-2 border-amber-400 dark:border-amber-500",
                      !hasCustomColor && !isDraft && colors.bgCard,
                      isDraft && "bg-amber-50 dark:bg-amber-900/20",
                      !isDraft && colors.borderPrimary,
                      colors.cardShadow
                    )}
                    style={hasCustomColor ? { backgroundColor: product.card_color } : undefined}
                    onClick={handleCardClick}
                  >
                    {/* Card Header with Settings Icon */}
                    <div className="flex items-start justify-between mb-4 flex-1">
                      <div className="flex-1">
                        {isDraft && (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-100 rounded mb-2">
                            Draft
                          </span>
                        )}
                        <h3 
                          className={cn(
                            "text-lg font-semibold mb-2 transition-colors",
                            !hasCustomColor && colors.textPrimary,
                            !hasCustomColor && colors.textAccentHover
                          )}
                          style={hasCustomColor ? { color: textColor } : undefined}
                        >
                          {displayName}
                        </h3>
                        <div 
                          className={cn("text-sm line-clamp-3", !hasCustomColor && colors.textSecondary)}
                          style={hasCustomColor ? { color: textColor, opacity: 0.85 } : undefined}
                        >
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <span>{children}</span>,
                              strong: ({ children }) => <strong className="font-semibold" style={{ color: 'inherit' }}>{children}</strong>,
                              em: ({ children }) => <em style={{ color: 'inherit' }}>{children}</em>,
                            }}
                          >
                            {displayDescription || ''}
                          </ReactMarkdown>
                        </div>
                      </div>
                      {/* Settings Icon - only for PMs and owners */}
                      {canSeeSettings && !isDraft && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-8 w-8",
                                  !hasCustomColor && colors.textMuted,
                                  "hover:bg-gray-200 dark:hover:bg-gray-700"
                                )}
                                style={hasCustomColor ? { color: textColor } : undefined}
                                onClick={(e) => openSettings(e, product)}
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>Product Settings</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    
                    {/* Footer with stats and archive button */}
                    {!isDraft && (
                    <div 
                      className="flex items-center justify-between gap-3 px-4 py-3 -mx-6 -mb-6 mt-auto rounded-b-lg"
                      style={{ 
                        backgroundColor: hasCustomColor ? darkenColor(product.card_color, 15) : undefined,
                        ...(hasCustomColor ? {} : {})
                      }}
                    >
                      <TooltipProvider delayDuration={200}>
                        <div className={cn(
                          "flex items-center gap-4 flex-1",
                          !hasCustomColor && "bg-gray-100 dark:bg-gray-800/50 px-4 py-2 -mx-4 -my-3 rounded-b-lg"
                        )}>
                          {/* Goals */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="flex items-center gap-1.5 cursor-default"
                                style={hasCustomColor ? { color: textColor, opacity: 0.9 } : undefined}
                              >
                                <Target 
                                  className="w-4 h-4" 
                                  style={hasCustomColor ? { color: textColor } : { color: '#22c55e' }} 
                                />
                                <span className={cn("text-sm font-medium", !hasCustomColor && colors.textSecondary)}>
                                  {product.goals_count ?? 0}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>Goals</p>
                            </TooltipContent>
                          </Tooltip>
                          
                          {/* Initiatives */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="flex items-center gap-1.5 cursor-default"
                                style={hasCustomColor ? { color: textColor, opacity: 0.9 } : undefined}
                              >
                                <Compass 
                                  className="w-4 h-4" 
                                  style={hasCustomColor ? { color: textColor } : { color: '#f59e0b' }} 
                                />
                                <span className={cn("text-sm font-medium", !hasCustomColor && colors.textSecondary)}>
                                  {product.initiatives_count ?? 0}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>Initiatives</p>
                            </TooltipContent>
                          </Tooltip>
                          
                          {/* Epics */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="flex items-center gap-1.5 cursor-default"
                                style={hasCustomColor ? { color: textColor, opacity: 0.9 } : undefined}
                              >
                                <Layers 
                                  className="w-4 h-4" 
                                  style={hasCustomColor ? { color: textColor } : { color: '#a855f7' }} 
                                />
                                <span className={cn("text-sm font-medium", !hasCustomColor && colors.textSecondary)}>
                                  {product.epics_count ?? 0}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>Epics</p>
                            </TooltipContent>
                          </Tooltip>
                          
                          {/* Features */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="flex items-center gap-1.5 cursor-default"
                                style={hasCustomColor ? { color: textColor, opacity: 0.9 } : undefined}
                              >
                                <FileText 
                                  className="w-4 h-4" 
                                  style={hasCustomColor ? { color: textColor } : { color: '#3b82f6' }} 
                                />
                                <span className={cn("text-sm font-medium", !hasCustomColor && colors.textSecondary)}>
                                  {product.features_count ?? 0}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>Features</p>
                            </TooltipContent>
                          </Tooltip>
                          
                          {/* Spacer to push archive button to right */}
                          <div className="flex-1" />
                          
                          {/* Archive Button - in footer, right side */}
                          {canCrudProducts && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  className={cn(
                                    "opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-black/10 dark:hover:bg-white/10",
                                    archiving === product.product_id && "opacity-100"
                                  )}
                                  style={hasCustomColor ? { color: textColor } : undefined}
                                  onClick={(e) => showArchiveConfirm(e, product)}
                                  disabled={archiving === product.product_id}
                                >
                                  {archiving === product.product_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Archive className={cn("w-4 h-4", !hasCustomColor && "text-gray-500 hover:text-amber-600 dark:hover:text-amber-400")} />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                <p>Archive Product</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TooltipProvider>
                    </div>
                    )}
                    
                    {isDraft && (
                      <div className="flex items-center justify-between pt-4 border-t border-amber-200 dark:border-amber-700">
                        <span className="text-sm text-amber-600 dark:text-amber-400">
                          Click to continue editing
                        </span>
                        {canCrudProducts && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  data-testid={`delete-draft-${product.product_id}`}
                                  className={cn(
                                    "opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30",
                                    deleting && "opacity-100"
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm({ show: true, product });
                                  }}
                                  disabled={deleting}
                                >
                                  {deleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                                  ) : (
                                    <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-600 dark:hover:text-red-400" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                <p>Delete Draft</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    )}
                  </Card>
                  );
                })}
              </div>
            )}

            {/* Archived Products Section */}
            {archivedProducts.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={cn(
                    "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                    colors.textSecondary,
                    "hover:text-gray-900 dark:hover:text-gray-100"
                  )}
                >
                  {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Archive className="w-4 h-4" />
                  Archived Products ({archivedProducts.length})
                </button>
                
                {showArchived && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {archivedProducts.map((product) => (
                      <Card
                        key={product.product_id}
                        className={cn(
                          "p-4 border opacity-70 hover:opacity-100 transition-opacity",
                          colors.bgCard,
                          colors.borderPrimary
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                              {product.name}
                            </h4>
                            <p className={cn("text-xs", colors.textMuted)}>
                              Archived {product.archived_at ? new Date(product.archived_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                          {canCrudProducts && (
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn("ml-4 shrink-0", colors.borderPrimary)}
                              onClick={(e) => handleUnarchive(e, product.product_id, product.name)}
                              disabled={archiving === product.product_id}
                            >
                              {archiving === product.product_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Restore
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Upgrade Modal */}
        <UpgradeContextModal
          isOpen={showUpgradeModal}
          onClose={closeModal}
          limitType="products"
          currentUsage={currentUsage}
          currentLimit={currentLimit}
          returnPath="/products"
          onUpgradeComplete={() => {
            closeModal();
            loadProducts();
          }}
        />
        
        {/* Archive Confirmation Modal */}
        <Dialog open={archiveConfirm.show} onOpenChange={(open) => !open && setArchiveConfirm({ show: false, product: null })}>
          <DialogContent className={cn(colors.bgCard, colors.borderPrimary)}>
            <DialogHeader>
              <DialogTitle className={colors.textPrimary}>Archive Product</DialogTitle>
              <DialogDescription className={colors.textSecondary}>
                Are you sure you want to archive <span className="font-semibold">{archiveConfirm.product?.name}</span>?
                <br /><br />
                Archived products can be restored later from the archived section.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setArchiveConfirm({ show: false, product: null })}
                className={colors.borderPrimary}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleArchive}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Draft Confirmation Modal */}
        <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, product: null })}>
          <DialogContent className={cn(colors.bgCard, colors.borderPrimary)}>
            <DialogHeader>
              <DialogTitle className={colors.textPrimary}>Delete Draft</DialogTitle>
              <DialogDescription className={colors.textSecondary}>
                Are you sure you want to delete <span className="font-semibold">{deleteConfirm.product?.draft_data?.canvas?.product?.name || 'this draft'}</span>?
                <br /><br />
                This action is permanent and cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setDeleteConfirm({ show: false, product: null })}
                className={colors.borderPrimary}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteDraft}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Product Settings Modal */}
        {showSettingsModal && settingsProduct && (
          <ProductSettingsModal
            open={showSettingsModal}
            onClose={() => {
              setShowSettingsModal(false);
              setSettingsProduct(null);
            }}
            product={settingsProduct}
            teamMembers={teamMembers}
            onProductUpdate={handleSettingsUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
